import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { fmtQty, GRADES, QC_RESULT_LABELS, type StockUnitRecord } from "@/types/inventory";

interface Row { defect: string; points: string; count: string }
const COMMON = ["Slub", "Hole", "Stain", "Broken end", "Weft bar", "Oil mark", "Crease"];

/** The suggested verdict from points per 100 (the usual four-point limits: up to 40 passes, up to 60 is a second) */
export const suggestResult = (p100: number) => (p100 <= 40 ? "PASS" : p100 <= 60 ? "SECOND" : "REJECT");

/** Inspect one roll: count defects at 1 to 4 points each, see the points per 100, record the verdict (which sets the grade) */
export function QcDialog({ unit, workOrderId, onClose, onSaved }: { unit: StockUnitRecord | null; workOrderId?: string; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [rows, setRows] = useState<Row[]>([{ defect: "", points: "1", count: "1" }]);
  const [qty, setQty] = useState("");
  const [result, setResult] = useState("PASS");
  const [touched, setTouched] = useState(false);
  const [apply, setApply] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!unit) return;
    setRows([{ defect: "", points: "1", count: "1" }]);
    setQty(String(Number(unit.quantity)));
    setResult("PASS");
    setTouched(false);
    setApply(true);
    setNotes("");
  }, [unit?.id]);

  const total = rows.reduce((a, r) => a + (Number(r.points) || 0) * (Number(r.count) || 0), 0);
  const q = Number(qty) || 0;
  const p100 = q > 0 ? Math.round(((total * 100) / q) * 100) / 100 : 0;
  useEffect(() => {
    if (!touched) setResult(suggestResult(p100));
  }, [p100, touched]);
  const set = (i: number, patch: Partial<Row>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    if (!unit) return;
    const defects = rows.filter((r) => r.defect.trim() && Number(r.count) > 0).map((r) => ({ defect: r.defect.trim(), points: Number(r.points), count: Number(r.count) }));
    if (rows.some((r) => !r.defect.trim() && Number(r.count) > 0 && rows.length > 1)) return error("Validation Error", "Name every defect or remove the empty row");
    if (!(q > 0)) return error("Validation Error", "Enter how much was inspected");
    setSaving(true);
    try {
      await api.post("/api/Qc/Insert", { unit_id: unit.id, work_order_id: workOrderId, inspected_qty: q, defects, result, apply_grade: apply, notes });
      success("Inspection saved", apply ? `${unit.unit_no} is now ${GRADES[result === "PASS" ? "FRESH" : result] || result}` : "");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!unit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Inspect roll {unit?.unit_no}</DialogTitle>
          <DialogDescription>{unit?.product?.name} · lot {unit?.lot?.lot_number} · {unit ? fmtQty(unit.quantity, unit.unit) : ""} · now {unit ? GRADES[unit.grade] || unit.grade : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 text-sm">
          <div className="space-y-1.5">
            <Label>Length inspected ({unit?.unit})</Label>
            <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className="h-10 w-40" />
          </div>
          <div className="space-y-1.5">
            <Label>Defects found</Label>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                <input list="qc-defects" className="col-span-6 h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring" placeholder="Defect (e.g. Slub)" value={r.defect} onChange={(e) => set(i, { defect: e.target.value })} />
                <select className="col-span-3 h-9 rounded-md border border-input bg-background px-2 text-sm" value={r.points} onChange={(e) => set(i, { points: e.target.value })}>
                  {[1, 2, 3, 4].map((p) => <option key={p} value={p}>{p} pt{p > 1 ? "s" : ""}</option>)}
                </select>
                <input className="col-span-2 h-9 rounded-md border border-input bg-background px-2 text-sm" inputMode="numeric" aria-label="How many" value={r.count} onChange={(e) => set(i, { count: e.target.value })} />
                <button type="button" className="col-span-1 text-muted-foreground hover:text-red-600 disabled:opacity-30" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="size-3.5 mx-auto" /></button>
              </div>
            ))}
            <datalist id="qc-defects">{COMMON.map((c) => <option key={c} value={c} />)}</datalist>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setRows([...rows, { defect: "", points: "1", count: "1" }])}><Plus className="size-3" /> Add defect</Button>
          </div>
          <div className="rounded-lg bg-muted/40 px-3 py-2 flex items-center justify-between">
            <span>{total} point{total === 1 ? "" : "s"} · <b>{p100}</b> per 100 {unit?.unit}</span>
            <span className="text-xs text-muted-foreground">Suggested: {QC_RESULT_LABELS[suggestResult(p100)]}</span>
          </div>
          <div className="space-y-1.5">
            <Label>Verdict</Label>
            <div className="flex gap-1.5">
              {Object.entries(QC_RESULT_LABELS).map(([k, v]) => (
                <button key={k} type="button" onClick={() => { setResult(k); setTouched(true); }} className={`h-9 px-4 rounded-lg text-sm font-semibold border ${result === k ? (k === "PASS" ? "bg-emerald-600 text-white border-emerald-600" : k === "SECOND" ? "bg-amber-500 text-white border-amber-500" : "bg-rose-600 text-white border-rose-600") : "bg-background hover:bg-muted border-border"}`}>{v}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2"><input type="checkbox" className="size-4" checked={apply} onChange={(e) => setApply(e.target.checked)} /> Set the roll's grade to match ({result === "PASS" ? "Fresh" : result === "SECOND" ? "Second" : "Reject"})</label>
          <div className="space-y-1.5"><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-10" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Save inspection</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
